import { Link } from "react-router-dom";
import { Mail, MessageCircle, MapPin, Leaf, Linkedin, Instagram } from "lucide-react";
import { useSite, waLinkFromNumber, formatPhone } from "@/context/SiteContext";

const COLS = [
  {
    title: "Solutions",
    links: [
      { to: "/product-development", label: "Product Development" },
      { to: "/food-consultancy", label: "Food Consultancy" },
      { to: "/raw-material-supply", label: "Raw Material Supply" },
      { to: "/franchise-solutions", label: "Franchise Solutions" },
    ],
  },
  {
    title: "Company",
    links: [
      { to: "/about", label: "About Us" },
      { to: "/brands", label: "Our Brands" },
      { to: "/contact", label: "Contact" },
    ],
  },
];

export default function SiteFooter() {
  const { content } = useSite();
  const phone = content.whatsapp;
  return (
    <footer
      data-testid="site-footer"
      className="bg-sand relative overflow-hidden border-t border-[var(--k-line)]"
    >
      <div className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full bg-[var(--k-olive-soft)] opacity-30 blur-[100px]" />
      <div className="container-k px-6 md:px-12 py-20 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Link to="/" className="flex items-center gap-3" data-testid="footer-brand">
              <div className="w-7 h-7 border border-[var(--k-green)] rotate-45 flex items-center justify-center">
                <div className="w-2 h-2 bg-[var(--k-green)] rotate-45" />
              </div>
              <span className="font-serif-display text-2xl text-[var(--k-text)]">KOSTRA</span>
            </Link>
            <p className="mt-5 font-serif-display italic text-xl text-[var(--k-text-2)] max-w-md leading-relaxed">
              {content.tagline}
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
              <Office label="Registered Office" value={content.noida_office} />
              <Office label="Gurgaon Office" value={content.gurgaon_office} />
            </div>
          </div>

          {COLS.map((col) => (
            <div key={col.title} className="md:col-span-2">
              <div className="font-mono-label text-[var(--k-gold)] mb-5">{col.title}</div>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      className="text-[var(--k-text-2)] text-sm hover:text-[var(--k-green)] transition-colors"
                      data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="md:col-span-3">
            <div className="font-mono-label text-[var(--k-gold)] mb-5">Connect</div>
            <a
              href={waLinkFromNumber(phone, "Hi KOSTRA — I'd like to learn more.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp"
              className="flex items-center gap-3 text-[var(--k-text)] text-sm hover:text-[var(--k-green)] transition-colors mb-3"
            >
              <MessageCircle className="w-4 h-4 text-[#25D366]" />
              <span>{formatPhone(phone)}</span>
            </a>
            <a
              href={`mailto:${content.email}`}
              data-testid="footer-email"
              className="flex items-center gap-3 text-[var(--k-text)] text-sm hover:text-[var(--k-green)] transition-colors mb-3"
            >
              <Mail className="w-4 h-4 text-[var(--k-green)]" />
              <span>{content.email}</span>
            </a>
            <div className="mt-7 flex items-center gap-4 text-[var(--k-muted)]">
              <a href="#" aria-label="Instagram" className="hover:text-[var(--k-green)]" data-testid="footer-instagram">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="#" aria-label="LinkedIn" className="hover:text-[var(--k-green)]" data-testid="footer-linkedin">
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[var(--k-line)] flex flex-col md:flex-row justify-between gap-3 text-[var(--k-muted)] text-xs">
          <span className="flex items-center gap-2">
            <Leaf className="w-3 h-3 text-[var(--k-olive)]" strokeWidth={1.5} />
            © {new Date().getFullYear()} KOSTRA Hospitality Pvt. Ltd. · All rights reserved.
          </span>
          <span className="font-mono-label">B2B Food Solutions · India</span>
        </div>
      </div>
    </footer>
  );
}

function Office({ label, value }) {
  return (
    <div className="flex gap-3">
      <MapPin className="w-4 h-4 text-[var(--k-green)] mt-0.5 shrink-0" strokeWidth={1.5} />
      <div>
        <div className="font-mono-label text-[var(--k-muted)] text-[0.55rem]">{label}</div>
        <div className="text-[var(--k-text)] text-sm mt-1">{value}</div>
      </div>
    </div>
  );
}
